import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { LoadingState } from "../../../../../components/primitives/feedback/LoadingState";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../../components/primitives/data-display/DataSequence";
import { DocumentLink } from "../../../../../components/domain/data-display/DocumentLink";
import { Note } from "../../../../../components/primitives/data-display/Note";
import { Statistic } from "../../../../../components/primitives/data-display/Statistic";
import { getBatchJobItemRowId } from "../../panel/batch_job_item_rows";
import {
  DOCUMENT_SECTIONS_REFRESH_TAB_ORDER,
  DOCUMENT_SECTIONS_REFRESH_TABS,
} from "./document_sections_refresh_tabs";
import type {
  DocumentSectionsRefreshPanelSummary,
  DocumentSectionsRefreshRow,
  UseDocumentSectionsRefreshPanelResult,
} from "./useDocumentSectionsRefreshPanel";

/** The header's counts, or a note until the job has finished. */
function SummaryHeader({ summary }: { summary: DocumentSectionsRefreshPanelSummary | null }) {
  if (!summary) {
    return <Note>The summary appears once the job has finished.</Note>;
  }
  return (
    <Stack direction="row" gap={4} flexWrap="wrap">
      <Statistic label="refreshed">{summary.refreshedCount}</Statistic>
      <Statistic label="skipped">{summary.skippedCount}</Statistic>
      <Statistic label="paragraphs embedded">{summary.paragraphsEmbedded}</Statistic>
    </Stack>
  );
}

/** What happened to a row's document: the paragraphs embedded, why it was skipped, or the failure. */
function OutcomeCell({ row }: { row: DocumentSectionsRefreshRow }) {
  const { result, item } = row;
  if (!result) {
    return item.error ? (
      <Typography variant="caption" color="error">
        {item.error}
      </Typography>
    ) : (
      <>—</>
    );
  }
  if (result.outcome === "skipped") {
    return (
      <Typography variant="caption" color="text.secondary">
        {result.reason ?? "Skipped"}
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="text.secondary">
      {result.paragraphCount} {result.paragraphCount === 1 ? "paragraph" : "paragraphs"} embedded
    </Typography>
  );
}

export interface DocumentSectionsRefreshPanelBodyProps {
  panel: UseDocumentSectionsRefreshPanelResult;
}

/** Presentational document_sections_refresh results panel: a summary of the run, then one tab per outcome,
 *  each a paginated list of documents. Read-only. */
export function DocumentSectionsRefreshPanelBody({ panel }: DocumentSectionsRefreshPanelBodyProps) {
  const columns: DataSequenceColumn<DocumentSectionsRefreshRow>[] = [
    {
      key: "subject",
      header: "Document",
      width: 260,
      cardRole: "primary",
      cell: (row) => <DocumentLink title={row.item.subjectTitle} url={row.item.subjectUrl} />,
    },
    {
      key: "outcome",
      header: "Result",
      cell: (row) => <OutcomeCell row={row} />,
    },
  ];

  return (
    <Stack gap={2}>
      <SummaryHeader summary={panel.summary} />

      <Tabs
        value={panel.tabs.activeTab}
        onChange={(_e, v: string) => panel.tabs.setActiveTab(v as typeof panel.tabs.activeTab)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
      >
        {DOCUMENT_SECTIONS_REFRESH_TAB_ORDER.map((key) => (
          <Tab key={key} label={DOCUMENT_SECTIONS_REFRESH_TABS[key].label} value={key} />
        ))}
      </Tabs>

      {panel.list.error && <Alert severity="error">{panel.list.error}</Alert>}

      {panel.list.loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={panel.list.rows}
          getRowId={getBatchJobItemRowId}
          emptyMessage="No documents in this list."
          pagination={panel.list.pagination}
        />
      )}
    </Stack>
  );
}
